import type { InfoShareSummary } from '../api/client';
import { resolveInfoShareLabel } from '../crypto/shareLabelUpload';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { tInfoShareStatus } from '../i18n/status';
import { canManageInfoShare, canRevokeInfoShare } from './infoShareManage';
import { buildSmsStatusFlow } from './smsStatusFlow';

export interface InfoShareListCallbacks {
  onOpenReplies: (shareId: string) => void;
  onManage: (shareId: string) => void;
  onRevoke: (shareId: string) => void;
  onDismissSmsDeliveryFailure?: (shareId: string, reference: string) => void;
}

interface ShareRowState {
  labelBlobId: string | null;
  displayLabel: string | null;
  displayStatus: string;
  expiresAt: string;
  createdAt: string;
  allowGuestReply: boolean;
  unreadReplyCount: number;
  multiUseAccess: boolean;
  wasOpened: boolean;
  smsDeliveryReference: string | null;
  smsDeliveryStatus: string | null;
  smsDeliveryErrorDescription: string | null;
}

const TERMINAL_STATUSES = new Set(['Expired', 'Revoked', 'Purged']);

export function isTerminalInfoShareStatus(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

export function sortInfoShares(shares: InfoShareSummary[]): InfoShareSummary[] {
  return [...shares].sort((left, right) => {
    const leftUnread = left.unreadReplyCount > 0 ? 1 : 0;
    const rightUnread = right.unreadReplyCount > 0 ? 1 : 0;
    if (leftUnread !== rightUnread) {
      return rightUnread - leftUnread;
    }

    return right.createdAt.localeCompare(left.createdAt);
  });
}

export class InfoShareListController {
  private readonly rowById = new Map<string, HTMLElement>();
  private readonly stateById = new Map<string, ShareRowState>();
  private readonly expiryTimers = new Map<string, number>();
  private readonly labelCache = new Map<string, string | null>();
  private hideTerminal = false;
  /** False until the first setShares(); avoids empty-copy flash before fetch. */
  private hasLoadedShares = false;
  private readonly unbindLocale = bindLocaleRefresh(() => this.refreshLocale());

  constructor(
    private readonly listEl: HTMLDivElement,
    private readonly emptyEl: HTMLElement,
    private readonly callbacks: InfoShareListCallbacks,
  ) {
    this.emptyEl.hidden = true;
  }

  dispose(): void {
    this.unbindLocale();

    for (const timer of this.expiryTimers.values()) {
      window.clearTimeout(timer);
    }

    this.expiryTimers.clear();
    this.rowById.clear();
    this.stateById.clear();
    this.hasLoadedShares = false;
    this.emptyEl.hidden = true;
  }

  setHideTerminal(hide: boolean): void {
    if (this.hideTerminal === hide) {
      return;
    }

    this.hideTerminal = hide;
    this.syncVisibilityAndOrder();
  }

  setShares(shares: InfoShareSummary[]): void {
    this.hasLoadedShares = true;
    const sorted = sortInfoShares(shares);
    const incomingIds = new Set(sorted.map((share) => share.id));

    for (const [shareId, row] of this.rowById) {
      if (!incomingIds.has(shareId)) {
        row.remove();
        this.rowById.delete(shareId);
        this.stateById.delete(shareId);
        this.clearExpiryTimer(shareId);
      }
    }

    if (sorted.length === 0) {
      this.listEl.replaceChildren();
      this.rowById.clear();
      this.stateById.clear();
      this.syncVisibilityAndOrder();
      return;
    }

    for (const share of sorted) {
      this.upsertShareRow(share);
    }

    this.syncVisibilityAndOrder();
  }

  upsertShare(share: InfoShareSummary, _position: 'prepend' | 'append' = 'append'): void {
    this.upsertShareRow(share);
    this.syncVisibilityAndOrder();
  }

  /** Insert or patch a row without syncing visibility (bulk load uses one sync). */
  private upsertShareRow(share: InfoShareSummary): void {
    const existing = this.rowById.get(share.id);
    if (existing) {
      const previous = this.stateById.get(share.id);
      this.patchShare({
        ...share,
        labelBlobId: share.labelBlobId ?? previous?.labelBlobId ?? null,
        wasOpened: share.wasOpened || previous?.wasOpened === true,
        createdAt: share.createdAt || previous?.createdAt || '',
      });
      return;
    }

    const row = this.createRow(share);
    this.rowById.set(share.id, row);
    this.stateById.set(share.id, this.toRowState(share));
    this.scheduleExpiry(share.id, share.expiresAt, share.displayStatus);
    void this.refreshLabelForShare(share.id, share.labelBlobId);
    applyI18n(row);
  }

  patchShare(share: InfoShareSummary): void {
    const row = this.rowById.get(share.id);
    if (!row) {
      this.upsertShare(share);
      return;
    }

    const previous = this.stateById.get(share.id) ?? this.toRowState(share);
    const next = this.toRowState(share, previous);

    if (previous.labelBlobId !== next.labelBlobId) {
      void this.refreshLabelForShare(share.id, next.labelBlobId);
    }

    if (
      previous.displayStatus !== next.displayStatus
      || previous.wasOpened !== next.wasOpened
    ) {
      this.patchStatusBadge(row, next.displayStatus, next.wasOpened);
    }

    if (previous.displayLabel !== next.displayLabel) {
      const titleEl = row.querySelector('.share-row__title') as HTMLSpanElement;
      titleEl.textContent = this.formatShareTitle(next.displayLabel);
    }

    if (previous.expiresAt !== next.expiresAt) {
      const expiresEl = row.querySelector('.share-row__expires') as HTMLSpanElement;
      expiresEl.textContent = new Date(next.expiresAt).toLocaleString();
      this.scheduleExpiry(share.id, next.expiresAt, next.displayStatus);
    } else if (previous.displayStatus !== next.displayStatus) {
      this.scheduleExpiry(share.id, next.expiresAt, next.displayStatus);
    }

    if (previous.unreadReplyCount !== next.unreadReplyCount) {
      this.patchUnreadBadge(row, next.unreadReplyCount);
    }

    if (
      previous.allowGuestReply !== next.allowGuestReply
      || previous.displayStatus !== next.displayStatus
      || previous.multiUseAccess !== next.multiUseAccess
    ) {
      this.patchActions(row, share);
    }

    if (
      previous.smsDeliveryStatus !== next.smsDeliveryStatus
      || previous.smsDeliveryErrorDescription !== next.smsDeliveryErrorDescription
      || previous.smsDeliveryReference !== next.smsDeliveryReference
    ) {
      this.patchSmsDeliveryBanner(row, share.id, next);
    }

    this.stateById.set(share.id, next);
    this.syncVisibilityAndOrder();
  }

  patchFromLiveSnapshot(snapshot: {
    shareId: string;
    displayStatus: string;
    expiresAt: string;
    multiUseAccess: boolean;
    allowGuestReply: boolean;
    unreadReplyCount: number;
    labelBlobId: string | null;
    wasOpened?: boolean;
  }): void {
    const previous = this.stateById.get(snapshot.shareId);
    const share: InfoShareSummary = {
      id: snapshot.shareId,
      serverToken: '',
      displayStatus: snapshot.displayStatus,
      createdAt: previous?.createdAt ?? '',
      expiresAt: snapshot.expiresAt,
      multiUseAccess: snapshot.multiUseAccess,
      allowGuestReply: snapshot.allowGuestReply,
      passwordMode: '',
      unreadReplyCount: snapshot.unreadReplyCount,
      labelBlobId: snapshot.labelBlobId,
      wasOpened: snapshot.wasOpened ?? previous?.wasOpened ?? false,
      smsDeliveryReference: previous?.smsDeliveryReference ?? null,
      smsDeliveryStatus: previous?.smsDeliveryStatus ?? null,
      smsDeliveryErrorDescription: previous?.smsDeliveryErrorDescription ?? null,
    };

    const row = this.rowById.get(snapshot.shareId);
    if (!row) {
      this.upsertShare(share, 'prepend');
      return;
    }

    if (!previous) {
      return;
    }

    this.patchShare(share);
  }

  patchUnreadCount(shareId: string, unreadReplyCount: number): void {
    const row = this.rowById.get(shareId);
    const previous = this.stateById.get(shareId);
    if (!row || !previous || previous.unreadReplyCount === unreadReplyCount) {
      return;
    }

    this.patchUnreadBadge(row, unreadReplyCount);
    this.stateById.set(shareId, { ...previous, unreadReplyCount });
    this.syncVisibilityAndOrder();
  }

  getShareState(shareId: string): ShareRowState | undefined {
    return this.stateById.get(shareId);
  }

  refreshLocale(): void {
    for (const [shareId, row] of this.rowById) {
      const state = this.stateById.get(shareId);
      if (!state) {
        continue;
      }

      const titleEl = row.querySelector('.share-row__title') as HTMLSpanElement;
      titleEl.textContent = this.formatShareTitle(state.displayLabel);

      this.patchStatusBadge(row, state.displayStatus, state.wasOpened);

      const expiresEl = row.querySelector('.share-row__expires') as HTMLSpanElement;
      expiresEl.textContent = new Date(state.expiresAt).toLocaleString();

      this.patchUnreadBadge(row, state.unreadReplyCount);
      this.patchSmsDeliveryBanner(row, shareId, state);
    }

    this.syncEmptyMessage(this.countVisibleRows());
  }

  markExpired(shareId: string): void {
    const row = this.rowById.get(shareId);
    const state = this.stateById.get(shareId);
    if (!row || !state || state.displayStatus === 'Expired') {
      return;
    }

    this.patchShare({
      id: shareId,
      serverToken: '',
      displayStatus: 'Expired',
      createdAt: state.createdAt,
      expiresAt: state.expiresAt,
      multiUseAccess: state.multiUseAccess,
      allowGuestReply: state.allowGuestReply,
      passwordMode: '',
      unreadReplyCount: state.unreadReplyCount,
      labelBlobId: state.labelBlobId,
      wasOpened: state.wasOpened,
      smsDeliveryReference: state.smsDeliveryReference,
      smsDeliveryStatus: state.smsDeliveryStatus,
      smsDeliveryErrorDescription: state.smsDeliveryErrorDescription,
    });
  }

  private formatShareTitle(displayLabel: string | null): string {
    return displayLabel?.trim() || t('info.owner.untitledShare');
  }

  private toRowState(share: InfoShareSummary, previous?: ShareRowState): ShareRowState {
    return {
      labelBlobId: share.labelBlobId,
      // Require previous: `?.labelBlobId === share.labelBlobId` is true when both are undefined.
      displayLabel:
        previous != null && previous.labelBlobId === share.labelBlobId
          ? previous.displayLabel
          : null,
      displayStatus: share.displayStatus,
      expiresAt: share.expiresAt,
      createdAt: share.createdAt || previous?.createdAt || '',
      allowGuestReply: share.allowGuestReply,
      unreadReplyCount: share.unreadReplyCount,
      multiUseAccess: share.multiUseAccess,
      wasOpened: share.wasOpened || previous?.wasOpened === true,
      smsDeliveryReference:
        share.smsDeliveryReference
        ?? previous?.smsDeliveryReference
        ?? null,
      smsDeliveryStatus:
        share.smsDeliveryStatus !== undefined
          ? share.smsDeliveryStatus
          : (previous?.smsDeliveryStatus ?? null),
      smsDeliveryErrorDescription:
        share.smsDeliveryErrorDescription !== undefined
          ? share.smsDeliveryErrorDescription
          : (previous?.smsDeliveryErrorDescription ?? null),
    };
  }

  hasActiveSmsDeliveryFailures(): boolean {
    for (const state of this.stateById.values()) {
      if (state.smsDeliveryStatus === 'failed' && state.smsDeliveryReference) {
        return true;
      }
    }
    return false;
  }

  findShareIdBySmsDeliveryReference(reference: string): string | null {
    for (const [shareId, state] of this.stateById) {
      if (state.smsDeliveryReference === reference) {
        return shareId;
      }
    }
    return null;
  }

  patchSmsDeliveryFailure(
    shareId: string,
    failure: {
      smsDeliveryStatus: string | null;
      smsDeliveryErrorDescription: string | null;
    },
  ): void {
    const previous = this.stateById.get(shareId);
    const row = this.rowById.get(shareId);
    if (!previous || !row) {
      return;
    }

    const next: ShareRowState = {
      ...previous,
      smsDeliveryStatus: failure.smsDeliveryStatus,
      smsDeliveryErrorDescription: failure.smsDeliveryErrorDescription,
    };
    this.stateById.set(shareId, next);
    this.patchSmsDeliveryBanner(row, shareId, next);
  }

  private setDisplayLabel(shareId: string, displayLabel: string | null): void {
    const row = this.rowById.get(shareId);
    const state = this.stateById.get(shareId);
    if (!row || !state) {
      return;
    }

    this.stateById.set(shareId, { ...state, displayLabel });
    const titleEl = row.querySelector('.share-row__title') as HTMLSpanElement;
    titleEl.textContent = this.formatShareTitle(displayLabel);
  }

  private async refreshLabelForShare(shareId: string, labelBlobId: string | null): Promise<void> {
    if (!labelBlobId) {
      this.setDisplayLabel(shareId, null);
      return;
    }

    const cacheKey = `${shareId}:${labelBlobId}`;
    if (this.labelCache.has(cacheKey)) {
      this.setDisplayLabel(shareId, this.labelCache.get(cacheKey) ?? null);
      return;
    }

    try {
      const label = await resolveInfoShareLabel(shareId, labelBlobId);
      this.labelCache.set(cacheKey, label);
      this.setDisplayLabel(shareId, label);
    } catch {
      this.labelCache.set(cacheKey, null);
      this.setDisplayLabel(shareId, null);
    }
  }

  private createRow(share: InfoShareSummary): HTMLElement {
    const row = document.createElement('article');
    row.className = 'share-row';
    row.dataset.shareId = share.id;
    row.innerHTML = `
      <div class="share-row__meta">
        <span class="share-row__title"></span>
        <div class="share-row__details">
          <span class="status-badge share-row__status"></span>
          <span class="muted share-row__opened-hint" hidden></span>
          <span class="muted share-row__expires">${new Date(share.expiresAt).toLocaleString()}</span>
        </div>
        <div class="share-row__sms-delivery" hidden></div>
      </div>
      <div class="share-row__actions"></div>
    `;

    const titleEl = row.querySelector('.share-row__title') as HTMLSpanElement;
    titleEl.textContent = this.formatShareTitle(null);
    this.patchStatusBadge(row, share.displayStatus, share.wasOpened);
    this.patchSmsDeliveryBanner(row, share.id, this.toRowState(share));

    this.renderActions(row, share);
    return row;
  }

  private patchSmsDeliveryBanner(row: HTMLElement, shareId: string, state: ShareRowState): void {
    const host = row.querySelector('.share-row__sms-delivery') as HTMLDivElement | null;
    if (!host) {
      return;
    }

    host.replaceChildren();
    const showFailure = state.smsDeliveryStatus === 'failed' && !!state.smsDeliveryReference;
    host.hidden = !showFailure;
    if (!showFailure || !state.smsDeliveryReference) {
      return;
    }

    const reference = state.smsDeliveryReference;
    const label = document.createElement('p');
    label.className = 'share-row__sms-delivery-label';
    label.setAttribute('data-i18n', 'info.owner.smsDeliveryFailed');
    label.textContent = t('info.owner.smsDeliveryFailed');

    const flow = buildSmsStatusFlow('failed', true, state.smsDeliveryErrorDescription);

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'button ghost';
    dismiss.setAttribute('data-i18n', 'info.owner.smsDeliveryDismiss');
    dismiss.textContent = t('info.owner.smsDeliveryDismiss');
    dismiss.addEventListener('click', () => {
      this.callbacks.onDismissSmsDeliveryFailure?.(shareId, reference);
    });

    host.append(label, flow, dismiss);
    applyI18n(host);
  }

  private patchStatusBadge(row: HTMLElement, displayStatus: string, wasOpened: boolean): void {
    const badge = row.querySelector('.share-row__status') as HTMLSpanElement;
    badge.textContent = tInfoShareStatus(displayStatus);

    const hint = row.querySelector('.share-row__opened-hint') as HTMLSpanElement;
    const showOpenedHint = displayStatus === 'Expired' || displayStatus === 'Purged';
    hint.hidden = !showOpenedHint;
    if (showOpenedHint) {
      hint.textContent = wasOpened
        ? t('info.owner.wasOpened')
        : t('info.owner.wasNotOpened');
    }
  }

  private renderActions(row: HTMLElement, share: InfoShareSummary): void {
    const actions = row.querySelector('.share-row__actions') as HTMLDivElement;
    actions.replaceChildren();

    if (share.allowGuestReply) {
      const repliesButton = document.createElement('button');
      repliesButton.type = 'button';
      repliesButton.className = 'button ghost button--with-badge';
      repliesButton.dataset.replies = share.id;
      repliesButton.innerHTML = `<span data-i18n="info.owner.replies"></span>`;
      this.patchUnreadBadgeOnButton(repliesButton, share.unreadReplyCount);
      repliesButton.addEventListener('click', () => this.callbacks.onOpenReplies(share.id));
      actions.append(repliesButton);
    }

    if (canManageInfoShare(share)) {
      const manageButton = document.createElement('button');
      manageButton.type = 'button';
      manageButton.className = 'button ghost';
      manageButton.dataset.manage = share.id;
      manageButton.innerHTML = `<span data-i18n="info.owner.manage"></span>`;
      manageButton.addEventListener('click', () => this.callbacks.onManage(share.id));
      actions.append(manageButton);
    }

    if (canRevokeInfoShare(share)) {
      const revokeButton = document.createElement('button');
      revokeButton.type = 'button';
      revokeButton.className = 'button ghost';
      revokeButton.dataset.revoke = share.id;
      revokeButton.innerHTML = `<span data-i18n="info.owner.revoke"></span>`;
      revokeButton.addEventListener('click', () => this.callbacks.onRevoke(share.id));
      actions.append(revokeButton);
    }

    applyI18n(actions);
  }

  private patchActions(row: HTMLElement, share: InfoShareSummary): void {
    const actions = row.querySelector('.share-row__actions') as HTMLDivElement;
    const repliesButton = actions.querySelector('[data-replies]') as HTMLButtonElement | null;
    const manageButton = actions.querySelector('[data-manage]') as HTMLButtonElement | null;
    const revokeButton = actions.querySelector('[data-revoke]') as HTMLButtonElement | null;

    const shouldShowReplies = share.allowGuestReply;
    const shouldShowManage = canManageInfoShare(share);
    const shouldShowRevoke = canRevokeInfoShare(share);

    if (shouldShowReplies && !repliesButton) {
      this.renderActions(row, share);
      applyI18n(actions);
      return;
    }

    if (!shouldShowReplies && repliesButton) {
      repliesButton.remove();
    }

    if (shouldShowManage && !manageButton) {
      this.renderActions(row, share);
      applyI18n(actions);
      return;
    }

    if (!shouldShowManage && manageButton) {
      manageButton.remove();
    }

    if (shouldShowRevoke && !revokeButton) {
      this.renderActions(row, share);
      applyI18n(actions);
      return;
    }

    if (!shouldShowRevoke && revokeButton) {
      revokeButton.remove();
    }
  }

  private patchUnreadBadge(row: HTMLElement, unreadCount: number): void {
    const repliesButton = row.querySelector('[data-replies]') as HTMLButtonElement | null;
    if (!repliesButton) {
      return;
    }

    this.patchUnreadBadgeOnButton(repliesButton, unreadCount);
  }

  private patchUnreadBadgeOnButton(button: HTMLButtonElement, unreadCount: number): void {
    const existing = button.querySelector('.button-badge');
    if (unreadCount <= 0) {
      existing?.remove();
      return;
    }

    const label = unreadCount > 99 ? '99+' : String(unreadCount);
    const title = t('info.owner.unreadBadgeTitle', { count: unreadCount });
    if (existing) {
      existing.textContent = label;
      existing.setAttribute('title', title);
      return;
    }

    const badge = document.createElement('span');
    badge.className = 'button-badge';
    badge.title = title;
    badge.textContent = label;
    button.append(badge);
  }

  private syncVisibilityAndOrder(): void {
    if (!this.hasLoadedShares) {
      this.emptyEl.hidden = true;
      return;
    }

    const ordered = [...this.stateById.entries()].sort(([, left], [, right]) => {
      const leftUnread = left.unreadReplyCount > 0 ? 1 : 0;
      const rightUnread = right.unreadReplyCount > 0 ? 1 : 0;
      if (leftUnread !== rightUnread) {
        return rightUnread - leftUnread;
      }

      return right.createdAt.localeCompare(left.createdAt);
    });

    let visibleCount = 0;
    for (const [shareId, state] of ordered) {
      const row = this.rowById.get(shareId);
      if (!row) {
        continue;
      }

      const hidden = this.hideTerminal && isTerminalInfoShareStatus(state.displayStatus);
      row.hidden = hidden;
      this.listEl.append(row);
      if (!hidden) {
        visibleCount += 1;
      }
    }

    this.syncEmptyMessage(visibleCount);
  }

  private countVisibleRows(): number {
    let visibleCount = 0;
    for (const [shareId, state] of this.stateById) {
      const row = this.rowById.get(shareId);
      if (!row) {
        continue;
      }
      const hidden = this.hideTerminal && isTerminalInfoShareStatus(state.displayStatus);
      if (!hidden) {
        visibleCount += 1;
      }
    }
    return visibleCount;
  }

  private syncEmptyMessage(visibleCount: number): void {
    if (!this.hasLoadedShares || visibleCount > 0) {
      this.emptyEl.hidden = true;
      this.emptyEl.removeAttribute('data-empty-state');
      this.emptyEl.removeAttribute('data-filtered-empty');
      this.emptyEl.removeAttribute('data-i18n');
      this.emptyEl.textContent = '';
      return;
    }

    const filtered = this.stateById.size > 0;
    const key = filtered ? 'info.owner.listFilteredEmpty' : 'info.owner.listEmpty';
    this.emptyEl.hidden = false;
    this.emptyEl.className = 'muted';
    this.emptyEl.textContent = t(key);
    this.emptyEl.setAttribute('data-i18n', key);
    if (filtered) {
      this.emptyEl.dataset.filteredEmpty = 'true';
      delete this.emptyEl.dataset.emptyState;
    } else {
      this.emptyEl.dataset.emptyState = 'true';
      delete this.emptyEl.dataset.filteredEmpty;
    }
  }

  private scheduleExpiry(shareId: string, expiresAt: string, displayStatus: string): void {
    this.clearExpiryTimer(shareId);

    if (displayStatus === 'Expired' || displayStatus === 'Revoked' || displayStatus === 'Purged') {
      return;
    }

    const expiresMs = new Date(expiresAt).getTime();
    const delay = expiresMs - Date.now();
    if (!Number.isFinite(delay) || delay <= 0) {
      this.markExpired(shareId);
      return;
    }

    const timer = window.setTimeout(() => {
      this.expiryTimers.delete(shareId);
      this.markExpired(shareId);
    }, delay);
    this.expiryTimers.set(shareId, timer);
  }

  private clearExpiryTimer(shareId: string): void {
    const timer = this.expiryTimers.get(shareId);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      this.expiryTimers.delete(shareId);
    }
  }
}
